import { FOREIGN_SCRIPT_DENY_URLS } from './lib/telemetry/foreignScriptOrigins';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { auth, functions } from './config/firebase';
import { registerSurveyPhotoResolver } from './lib/survey/surveyPhotoResolver';
import { registerOfflineCacheClearer } from './lib/firebase-auth';
import { wipePersistedServerDocsCopies } from './components/forms/public/dataSource/serverDocsCopy/ServerDocsCopyRegistrar';
import { bindTelemetryAuthGate } from './lib/telemetry/authGateBinding';
import { initializeErrorLogging } from './lib/datahub/errorLogging';
import { initServiceWorker } from './lib/offline/serviceWorkerUpdate';
import { escapeToServerIfHandled } from './lib/offline/serverPathEscape';
import { buildSentryRelease, buildVersionTags } from './lib/telemetry/buildIdentity';
import {
  shouldInitSentry,
  createProductionOnlyBeforeSend,
} from './lib/telemetry/sentryEnvironmentGate';
import {
  scrubBreadcrumbUrls,
  scrubReplayEventUrls,
  scrubReplayRecordingEvent,
  scrubSpan,
  withScrubbedEventUrls,
} from './lib/telemetry/scrubUrl';
import { SENTRY_DATA_COLLECTION } from './lib/telemetry/sentryDataCollection';
import { makeOfflineAwareTransport } from './lib/telemetry/offlineTransport';
import { withCallableFailureTags } from './lib/telemetry/callableFailureContext';
import appVersionInfo from './generated/appVersion.json';
import { setUpdateTelemetrySink } from './appVersion/updateTelemetry';
import { createWebVitalSender } from './lib/telemetry/webVitals';

// Initialize Sentry error monitoring (must be before React render and error logging)
//
// THE GATE IS NOT "IS A DSN PRESENT" (OFFICEHUB-CLIENT-65). It used to be, and
// because client/.env carries the PRODUCTION DSN and CRA loads it for
// `npm start` exactly as for `npm run build`, 53% of this project's error
// events in the week to 2026-09-21 came from local and E2E runs. See
// lib/telemetry/sentryEnvironmentGate.ts for the measurement and the layers.
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const sentryEnvironment = process.env.NODE_ENV || 'production';
if (
  shouldInitSentry({
    dsn: sentryDsn,
    mode: process.env.NODE_ENV,
    hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
  })
) {
  Sentry.init({
    dsn: sentryDsn,
    // Errors raised with no signal wait on the device and send on reconnect (offlineTransport.ts).
    transport: makeOfflineAwareTransport(Sentry.makeFetchTransport, Sentry.makeBrowserOfflineTransport),
    environment: sentryEnvironment,
    // Layer 2. Layer 1 (the gate above) already refuses to initialise off a
    // real deployment; this refuses to SEND anything not stamped production,
    // and counts what it refuses so the filter can never look like silence.
    // #6018 review: the event's own URLs (request.url, route) are scrubbed before the gate.
    beforeSend: withScrubbedEventUrls(withCallableFailureTags(createProductionOnlyBeforeSend())),
    // #6018 review: performance data carries request URLs too (http.client spans, routes).
    // Sentry 11: spans stream, so `beforeSendTransaction` is never invoked and the
    // segment span (the former transaction) is scrubbed by `beforeSendSpan` below.
    beforeSendSpan: scrubSpan,
    // Sentry 11 collects cookies, bodies, headers and GenAI I/O unless told not to.
    dataCollection: SENTRY_DATA_COLLECTION,
    // Sentry 11 defaults this to true, which would mark a session errored on every
    // captureMessage (release health); keep the v10 behaviour.
    attachStacktrace: false,
    // #4557: no query string (a signed upload URL's credential) leaves in a breadcrumb.
    beforeBreadcrumb: scrubBreadcrumbUrls,
    // Previously read a build-time Sentry release env var that is SET NOWHERE in
    // this repo (#4022) -- no CI step, no .env, no build script -- so it always
    // fell through to the literal string 'local' on every production report. A
    // constant cannot distinguish one build from another, so no error was ever
    // attributable to a release. The real stamp is generated by
    // scripts/app-version/gen-version.mjs on every build and was sitting unused
    // in ./generated/appVersion.json. (The old variable name is deliberately not
    // repeated here: clientEntriesWired.test.ts greps entry files for it, and
    // that guard is intentionally dumb and strict.)
    release: buildSentryRelease(appVersionInfo),
    initialScope: { tags: buildVersionTags(appVersionInfo) },
    tracesSampleRate: 0.02,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Text masked like every other app: replays run on every error and the page is a customer or billing screen; media blocked: a survey photo link carries a download token.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true, beforeAddRecordingEvent: scrubReplayRecordingEvent }),
      // Release health (#stale-clients): a SESSION per page load, which is what
      // lets Sentry report users + adoption per release WITHOUT an error being
      // thrown. `release` above is unchanged and is what sessions attribute to.
      //
      // THIS LINE IS A PIN, NOT A NEW CAPABILITY -- measured 2026-09-20, not
      // assumed. In the installed SDK `browserSessionIntegration()` is already
      // part of `getDefaultIntegrations()` (@sentry/browser, build/npm/esm/prod/
      // sdk.js); this init passes `integrations` as an ARRAY, which MERGES with
      // the defaults rather than replacing them (only the FUNCTION form
      // replaces); and no init in this repo sets `defaultIntegrations`. The
      // integration is present in the deployed bundle on all four hosts. So
      // session tracking was ALREADY active before this line existed.
      //
      // What naming it explicitly buys is durability: a later switch to the
      // function form of `integrations`, a `defaultIntegrations` override, or
      // an SDK revision that drops it from the defaults can no longer remove
      // release health silently.
      //
      // It follows that this line does NOT explain why Sentry release health
      // shows no session data for these projects. That cause is NOT established
      // -- see the PR body; do not read this comment as a fix for it.
      Sentry.browserSessionIntegration(),
    ],
    // Signatures that are known-benign AND self-recovering. Everything here has a
    // stated reason; nothing is here because it was merely noisy.
    //
    // Why this list exists at all: without it these recur daily, and because Sentry
    // AUTO-UNRESOLVES a resolved issue the moment it recurs, every triage sweep
    // re-resolves the same issues forever. On 2026-08-20 six of the top issues were
    // `substatus: regressed` — already resolved by an earlier sweep — and between
    // them accounted for ~479 events against 10 users, burying the four real bugs in
    // the same list. Filtering at the SDK is the only thing that ends that loop.
    //
    // DELIBERATELY NOT INCLUDED, though the legacy Firestore-era IGNORE_PATTERNS
    // list carries them: /^internal$/, /permission-denied/, /missing or insufficient
    // permissions/, and generic HTTP 5xx. Those are exactly the signatures of THREE
    // of the four real bugs found in the 2026-08-20 sweep — an inspection callable
    // returning `internal`, a mis-gated callable returning permission-denied, and a
    // platform 500 on a Drive listing. Adopting that list wholesale would have
    // suppressed the findings instead of surfacing them.
    // Scripts that are never ours (browser extensions, Vercel insights); first-party and app:// code keep reporting.
    denyUrls: [...FOREIGN_SCRIPT_DENY_URLS],
    ignoreErrors: [
      // NOT FILTERED, deliberately — 'Failed to obtain exclusive access to the
      // persistence layer' and 'INTERNAL ASSERTION FAILED: Unexpected state
      // (ID: b815)' were both suppressed here by #3693 as benign fallback noise.
      // b815 was never benign: it is raised from a poisoned Firestore AsyncQueue
      // and bricks every Firestore-backed page in the tab until reload (#3775).
      //
      // The two signatures are NOT equivalent, and the difference matters when
      // triaging a recurrence:
      //
      //   'Failed to obtain exclusive access to the persistence layer' is now
      //   genuinely IMPOSSIBLE. That string exists only in the SDK's
      //   IndexedDbPersistence, and this app no longer takes an IndexedDB lock
      //   (see config/firebase.ts). If it reappears, the cache change did not
      //   take effect.
      //
      //   b815 is NOT impossible. It is a GENERIC poisoned-AsyncQueue assertion:
      //   the SDK stores the first rejection from any enqueued operation and
      //   every later enqueue re-asserts. Losing the IndexedDB lease was the only
      //   trigger ever observed here, and removing the lock removes it — but a
      //   watch-stream failure, the auth-token-refresh listener, or an internal
      //   hardAssert can still poison the queue. So a b815 recurrence means
      //   SOMETHING ELSE poisoned it, NOT that the cache change failed.
      //   ModuleErrorBoundary now auto-reloads once on b815 so a poisoned queue
      //   cannot brick the tab regardless of which operation caused it.
      //
      // Volume is not a concern at the measured rate: 313 b815 events across 89
      // days in the `error_events` sink. Treat that as a FLOOR — that sink
      // throttles to 10 errors/min and dedupes per fingerprint for 60s — but the
      // worst session (77 events over 3 days) was nowhere near saturating it, so
      // the real figure is close. The "479 events" that motivated #3693 was the
      // whole ignore-list, not this signature.
      //
      // IndexedDB torn down while the page is unloading. Nothing is lost; the next
      // load reopens it.
      'Database is closing',
      'Database is hidden',
      // A new build replaced the chunk this tab was holding. The reload resolves it.
      'ChunkLoadError',
      'Loading chunk',
      // The remote-config SDK opens IndexedDB behind a promise it floats
      // internally, so when the browser tears the connection down mid-open
      // ("The connection was closed") the rejection surfaces as an
      // unhandled-rejection event that NO app code can catch — the app-level
      // caller's own await is caught separately in useIssueTriageFlags, which
      // retries once and falls back to compiled-in defaults. A local
      // IndexedDB-open failure cannot express a server or app fault, so this
      // signature carries no signal (OFFICEHUB-CLIENT-3E / -5A).
      'remoteconfig/storage-open',
      // The build-version poller missing a beat. It retries on its next tick.
      /Network error: GET .*\/version\.json/,
      // Benign browser layout notification, not an application error.
      'ResizeObserver loop',
    ],
  });
  // #6018 review: replay events carry page URLs in `urls`; no beforeSendReplay exists.
  Sentry.addEventProcessor(scrubReplayEventUrls);
}

// Route update-path breadcrumbs (banner shown / dismissed / update accepted) to
// Sentry. Wired HERE rather than inside the shared appVersion module, which must
// not import @sentry/* -- see updateTelemetry.ts.
setUpdateTelemetrySink((crumb) => Sentry.addBreadcrumb(crumb));

// Initialize error logging (global handlers for uncaught errors + rejections)
initializeErrorLogging();

// Logout and a different user signing in on this device delete the persisted generators copy (firebase-auth.ts clearOfflineCache).
registerOfflineCacheClearer(wipePersistedServerDocsCopies);
registerSurveyPhotoResolver(); // before the first render: a tile drawn earlier would keep the tokenless link

// A payment link, shared report or other address hosting answers itself was handed to the app by a
// service worker that predates the navigation denylist (2026-10-17). Do not render the app or
// re-register a worker: unregister it and reload the address from the network, once.
// See lib/offline/serverPathEscape.ts.
const handingAddressBackToServer = escapeToServerIfHandled();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
if (!handingAddressBackToServer) {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Capture Web Vitals and store in Firestore for the Performance tab (#4022).
// Now routed through the shared sender so all four PWAs report identically and
// cannot drift. The inlined version here discarded its rejection with an empty
// catch — the same swallow already removed from usageTracking — which made a
// failing logPerformanceMetric indistinguishable from "the page was never slow".
// That is not hypothetical: logPerformanceMetric is one of the functions #3934
// found being OOM-killed, so it HAS been failing silently. Drops are now counted.
// (The literal swallow syntax is deliberately not written out here:
// clientEntriesWired.test.ts greps entry files for it, and that guard is
// intentionally dumb and strict.)
reportWebVitals(createWebVitalSender(functions));
// Report Firebase auth state to the telemetry gate.
// logPerformanceMetric and logUsageEvent are withOrgAuth callables, and both
// reportWebVitals (module load) and trackPageView (first route) fire before
// auth resolves -- which returned 401 on every page load. The gate holds those
// events until this binding says auth has resolved, then flushes them; if it
// resolves with no user they are dropped and COUNTED, never silently.
// Order relative to reportWebVitals does not matter: anything emitted before
// this line is buffered, which is the whole point of the gate's third state.
bindTelemetryAuthGate(auth);

// Register the app-shell service worker for offline reads (open an already-viewed
// survey with no signal). This REPLACES the old blanket `unregister()`, which was
// there because CRA's default precache SW served a stale index.html on every
// deploy ("Page Not Found" / old code until manual cache clear). The new worker
// avoids that: navigations are network-first (online users always get fresh HTML
// + chunk hashes) and new versions WAIT rather than skipWaiting mid-session
// (protects in-flight survey edits — audit D3). See service-worker.ts and
// lib/offline/serviceWorkerUpdate.ts. Only active in production builds.
if (!handingAddressBackToServer) initServiceWorker();
